import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ReloadOutlined, InboxOutlined } from "@ant-design/icons";
import { Alert, Button, Spin, Typography, Badge } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useCallback } from "react";
import OrderCardCompact from "./OrderCardCompact";
import { getRootStore } from "@/stores/RootStore";
import { orderService } from "@/services/OrderService";
import styles from "@/styles/Dashboard/UnassignedOrdersBlock.module.less";
const { Title } = Typography;
// Интервал автообновления (60 секунд)
const REFRESH_INTERVAL = 60000;
const UnassignedOrdersBlock = observer(() => {
    const { dashboardStore } = getRootStore();
    const scrollContainerRef = useRef(null);
    // Загрузка заказов с сохранением позиции скролла
    const loadOrders = useCallback(async () => {
        // Сохраняем позицию скролла
        const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
        dashboardStore.setOrdersLoading(true);
        dashboardStore.setOrdersError(null);
        try {
            // Используем специальный метод для получения заказов без маршрута
            // Метод использует SQL View с LEFT JOIN route_points WHERE rp.id IS NULL
            const unassignedOrders = await orderService.getUnassignedOrders();
            // Умное обновление через store
            dashboardStore.smartUpdateOrders(unassignedOrders);
        }
        catch (err) {
            dashboardStore.setOrdersError(err instanceof Error ? err.message : "Ошибка загрузки заказов");
        }
        finally {
            dashboardStore.setOrdersLoading(false);
            // Восстанавливаем позицию скролла (в следующем тике после рендера)
            requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollTop;
                }
            });
        }
    }, [dashboardStore]);
    // Начальная загрузка и автообновление
    useEffect(() => {
        loadOrders();
        const intervalId = setInterval(() => {
            loadOrders();
        }, REFRESH_INTERVAL);
        return () => {
            clearInterval(intervalId);
        };
    }, [loadOrders]);
    // Обработчик выбора заказа
    const handleOrderClick = (orderId) => {
        dashboardStore.selectOrder(orderId);
    };
    // Данные из store
    const orders = dashboardStore.ordersArray;
    const isLoading = dashboardStore.ordersLoading;
    const error = dashboardStore.ordersError;
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.header, children: [_jsx(Badge, { count: dashboardStore.ordersCount, overflowCount: 99, children: _jsx(Title, { level: 5, className: styles.title, children: "\u041D\u0435\u0440\u0430\u0441\u043F\u0440\u0435\u0434\u0435\u043B\u0451\u043D\u043D\u044B\u0435 \u0437\u0430\u043A\u0430\u0437\u044B" }) }), _jsx(Button, { type: "text", icon: _jsx(ReloadOutlined, {}), onClick: loadOrders, loading: isLoading, title: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })] }), _jsxs("div", { className: styles.content, ref: scrollContainerRef, children: [error && (_jsx(Alert, { message: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438", description: error, type: "error", showIcon: true, closable: true, onClose: () => dashboardStore.setOrdersError(null), action: _jsx(Button, { size: "small", onClick: loadOrders, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }), className: styles.alert })), isLoading && !error && orders.length === 0 && (_jsx("div", { className: styles.loadingContainer, children: _jsx(Spin, { tip: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0437\u0430\u043A\u0430\u0437\u043E\u0432..." }) })), !isLoading && !error && orders.length === 0 && (_jsxs("div", { className: styles.emptyState, children: [_jsx(InboxOutlined, { className: styles.emptyIcon }), _jsx("p", { children: "\u041D\u0435\u0442 \u043D\u0435\u0440\u0430\u0441\u043F\u0440\u0435\u0434\u0435\u043B\u0451\u043D\u043D\u044B\u0445 \u0437\u0430\u043A\u0430\u0437\u043E\u0432" })] })), orders.length > 0 && (_jsx("div", { className: styles.ordersList, children: orders.map((order) => (_jsx(OrderCardCompact, { order: order, isSelected: dashboardStore.selectedOrderId === order.id, isUpdated: dashboardStore.isOrderUpdated(order.id), onClick: () => handleOrderClick(order.id) }, order.id))) }))] })] }));
});
UnassignedOrdersBlock.displayName = "UnassignedOrdersBlock";
export default UnassignedOrdersBlock;
